'use client';

import * as amplitude from '@amplitude/analytics-browser';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { getPageTypeFromPathname, isHouseSettingsPathname } from '@/utils/analytics/amplitudeEvents';
import { usePathname } from '@/navigation-client';

export type AmplitudeContextType = {
    amplitude: typeof amplitude;
};

const AmplitudeContext = createContext<AmplitudeContextType | null>(null);

// Module-level flag prevents duplicate plugin registration when AmplitudeProvider
// remounts (e.g. Next.js route group transitions) or when React Strict Mode
// double-invokes effects. Each amplitude.init() call appends a new Destination
// plugin to the SDK pipeline — calling it N times results in each track() firing
// N times. This flag ensures init runs exactly once per page load.
let isAmplitudeInitialized = false;

// Resets the initialization flag — only intended for use in tests.
export const resetAmplitudeForTesting = () => {
    isAmplitudeInitialized = false;
};

export function useAmplitude(): AmplitudeContextType | null {
    return useContext(AmplitudeContext);
}

export function AmplitudeProvider({
    amplitudeApiKey,
    children,
}: Readonly<{
    amplitudeApiKey: string;
    children: React.ReactNode;
}>) {
    const pathname = usePathname();
    const lastTrackedPathnameRef = useRef<string | null>(null);

    useEffect(() => {
        if (!amplitudeApiKey) {
            // eslint-disable-next-line no-console
            console.warn(
                'Amplitude initialization skipped: No API key provided. Add NEXT_PUBLIC_AMPLITUDE_API_KEY to your environment variables.'
            );
            return;
        }

        if (isAmplitudeInitialized) {
            return;
        }

        // Page views are tracked manually by the effect below so we can
        // guarantee Page Type is attached on every event. Autocaptured page
        // views aren't reliably enrichable for the very first event fired
        // during init(), and disabling them lets us own the entire payload.
        amplitude.init(amplitudeApiKey, {
            autocapture: {
                attribution: false,
                elementInteractions: false,
                fileDownloads: false,
                formInteractions: false,
                pageViews: false,
                sessions: true,
            },
        });

        isAmplitudeInitialized = true;
    }, [amplitudeApiKey]);

    // Manual `[Amplitude] Page Viewed` tracking. Fires on initial mount and on
    // every Next.js client-side navigation. Always attaches Page Type
    // (defaults to 'Other' for unrecognised routes) and replicates the
    // standard properties Amplitude's autocapture plugin would have produced
    // so dashboards can still query Page Domain/Path/Title/URL.
    useEffect(() => {
        if (!amplitudeApiKey || !isAmplitudeInitialized) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }
        if (lastTrackedPathnameRef.current === pathname) {
            return;
        }
        // House Settings owns page views via trackHouseSettingsPageViewed (SP2-5054).
        if (isHouseSettingsPathname(pathname)) {
            lastTrackedPathnameRef.current = pathname;
            return;
        }
        lastTrackedPathnameRef.current = pathname;

        const pageType = getPageTypeFromPathname(pathname) ?? 'Other';
        const href = window.location.href;
        const url = href.split('?')[0];

        amplitude.track('[Amplitude] Page Viewed', {
            '[Amplitude] Page Domain': window.location.hostname,
            '[Amplitude] Page Location': href,
            '[Amplitude] Page Path': window.location.pathname,
            '[Amplitude] Page Title': document.title,
            '[Amplitude] Page URL': url,
            'Page Type': pageType,
        });
    }, [amplitudeApiKey, pathname]);

    const value = useMemo(() => ({ amplitude }), []);

    return <AmplitudeContext.Provider value={value}>{children}</AmplitudeContext.Provider>;
}
